import { CREDIT_PLANS } from "@super-resume-new/auth/lib/plans";
import { Button } from "@super-resume-new/ui/components/button";
import { cn } from "@super-resume-new/ui/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  ArrowRightIcon,
  CheckIcon,
  CoinsIcon,
  Loader2Icon,
  RefreshCwIcon,
  WalletIcon,
  Wand2Icon,
  ZapIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

import type { ActivePlan } from "@/functions/get-checkout-status";

import { PageLayout } from "@/components/page-layout";
import { getCheckoutStatus } from "@/functions/get-checkout-status";
import { seo } from "@/utils/seo";

/** Dodo appends these to the configured return URL. All are best-effort. */
interface SuccessSearch {
  subscription_id?: string;
  payment_id?: string;
  status?: string;
  email?: string;
}

/** Stop polling for the webhook after this long and tell the user to check back. */
const POLL_TIMEOUT_MS = 30_000;

/** A purchase grant this recent belongs to the checkout we just came back from. */
const RECENT_PURCHASE_MS = 15 * 60_000;

/**
 * Dodo statuses that mean no money moved. Anything else — including the
 * undocumented in-between states — is treated as pending, and the credit grant
 * decides. Treating every unknown status as a failure showed "Payment not
 * completed" on successful checkouts.
 */
const FAILED_STATUSES = new Set([
  "failed",
  "cancelled",
  "canceled",
  "expired",
  "requires_payment_method",
  "requires_customer_action",
]);

export const Route = createFileRoute("/success")({
  head: () =>
    seo({
      title: "Payment Successful | JobCues",
      description: "Your JobCues plan is active and your monthly credits are on the way.",
      path: "/success",
      noindex: true,
    }),
  component: SuccessPage,
  validateSearch: (search: Record<string, unknown>): SuccessSearch => ({
    subscription_id: typeof search.subscription_id === "string" ? search.subscription_id : undefined,
    payment_id: typeof search.payment_id === "string" ? search.payment_id : undefined,
    status: typeof search.status === "string" ? search.status : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
  }),
});

type Phase = "failed" | "pending" | "granted" | "delayed";

/** Copy key: "granted" splits by whether the purchase left a subscription behind. */
type Copy = Phase | "topup";

function SuccessPage() {
  const { subscription_id, payment_id, status, email } = Route.useSearch();
  const queryClient = useQueryClient();

  // Dodo redirects as soon as the charge clears, but credits are granted by the
  // webhook, which lands a moment later — sometimes before this page even
  // mounts. Poll for the purchase ledger entry rather than a balance change, so
  // a grant that already landed (or a page reload) still reads as granted.
  const [timedOut, setTimedOut] = useState(false);

  const failed = status !== undefined && FAILED_STATUSES.has(status);

  const { data } = useQuery({
    queryKey: ["checkout-status"],
    queryFn: () => getCheckoutStatus(),
    enabled: !failed,
    // Stop once the grant shows up, and stop at the timeout so a tab left open
    // does not poll the ledger forever.
    refetchInterval: (query) => (query.state.data?.purchasedAt || timedOut ? false : 3000),
    staleTime: 0,
  });

  const balance = data?.balance;
  const plan = data?.plan ?? null;
  const granted = data?.purchasedAt != null && Date.now() - data.purchasedAt < RECENT_PURCHASE_MS;

  useEffect(() => {
    if (failed) return;
    const timer = setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [failed]);

  // Refresh the header balance once the credits land.
  useEffect(() => {
    if (granted) void queryClient.invalidateQueries({ queryKey: ["credits"] });
  }, [granted, queryClient]);

  const phase: Phase = failed ? "failed" : granted ? "granted" : timedOut ? "delayed" : "pending";
  const reference = subscription_id ?? payment_id;

  return (
    <PageLayout id="main-content" className="max-w-3xl">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <Hero phase={phase} status={status} balance={balance} plan={plan} />

        {phase !== "failed" && (
          <>
            <hr className="border-t border-border" />
            <StatStrip phase={phase} balance={balance} email={email} plan={plan} />
          </>
        )}

        <hr className="border-t border-border" />
        <NextSteps phase={phase} />

        {reference && (
          <>
            <hr className="border-t border-border" />
            <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 sm:px-10">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Reference
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">{reference}</span>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}

const HERO: Record<Copy, { title: string; body: string }> = {
  failed: {
    title: "Payment not completed",
    body: "You have not been charged for an incomplete checkout. Nothing was added to your account.",
  },
  pending: {
    title: "Payment received",
    body: "Setting up your plan and adding this month's credits. This usually takes a few seconds.",
  },
  granted: {
    title: "Your plan is active",
    body: "Your credits are on your account and refresh automatically on every billing date.",
  },
  // A top-up leaves no subscription behind, so nothing renews — and unlike plan
  // credits, these do not expire.
  topup: {
    title: "Credits added",
    body: "Your top-up credits are on your account. They never expire, so they stay there until you spend them.",
  },
  delayed: {
    title: "Payment received",
    body: "Your credits are taking longer than usual to appear. They will land shortly — check the credits page in a minute, and contact us if they do not.",
  },
};

function Hero({
  phase,
  status,
  balance,
  plan,
}: {
  phase: Phase;
  status?: string;
  balance?: number;
  plan: ActivePlan | null;
}) {
  const { title, body } = HERO[phase === "granted" && !plan ? "topup" : phase];

  return (
    <div className="px-6 py-12 text-center sm:px-10 sm:py-16">
      <div
        className={cn(
          "mx-auto flex size-14 items-center justify-center rounded-full border",
          phase === "failed"
            ? "border-amber-500/30 bg-amber-500/10 text-amber-500"
            : phase === "pending"
              ? "border-border bg-muted text-muted-foreground"
              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500",
        )}
      >
        {phase === "failed" ? (
          <AlertCircleIcon className="size-6" aria-hidden="true" />
        ) : phase === "pending" ? (
          <Loader2Icon className="size-6 animate-spin" aria-hidden="true" />
        ) : (
          <CheckIcon className="size-6" aria-hidden="true" />
        )}
      </div>

      <h1 className="mt-6 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
        {title}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">{body}</p>

      {phase === "failed" && status && (
        <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          Status: {status}
        </p>
      )}

      {phase === "granted" && balance !== undefined && (
        <p
          className="mt-8 font-mono text-6xl font-semibold tabular-nums text-foreground"
          aria-live="polite"
        >
          {balance}
          <span className="ml-2 align-middle text-xs font-normal tracking-wider text-muted-foreground uppercase">
            credits
          </span>
        </p>
      )}
    </div>
  );
}

function StatStrip({
  phase,
  balance,
  email,
  plan,
}: {
  phase: Phase;
  balance?: number;
  email?: string;
  plan: ActivePlan | null;
}) {
  const planName = plan ? CREDIT_PLANS.find((p) => p.slug === plan.slug)?.name : undefined;
  // A one-time top-up leaves no subscription behind, so there is nothing to renew.
  const renews = plan?.nextBillingDate
    ? new Date(plan.nextBillingDate).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : plan
      ? "Monthly"
      : "One-time";

  return (
    <dl className="grid divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      <Stat
        icon={WalletIcon}
        label="Available credits"
        value={phase === "granted" && balance !== undefined ? String(balance) : "—"}
      />
      <Stat icon={RefreshCwIcon} label={planName ? `${planName} renews` : "Renews"} value={renews} />
      <Stat icon={CoinsIcon} label="Billed to" value={email ?? "Your account"} />
    </dl>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof WalletIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="px-6 py-5 sm:px-8">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="mr-1.5 inline size-3.5 align-[-0.15em]" aria-hidden="true" />
        {label}
      </dt>
      <dd className="mt-1.5 truncate font-mono text-sm tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

function NextSteps({ phase }: { phase: Phase }) {
  if (phase === "failed") {
    return (
      <div className="flex flex-col gap-3 px-6 py-6 sm:flex-row sm:px-10">
        <Button className="sm:flex-1" render={<Link to="/credits" />}>
          Back to plans
        </Button>
        <Button variant="outline" className="sm:flex-1" render={<Link to="/contact" />}>
          Contact support
        </Button>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 sm:px-10">
      <p className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        What&apos;s next
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <StepLink
          to="/resume-tailor"
          icon={Wand2Icon}
          title="Tailor a resume"
          body="Rewrite against one job description in about 15 seconds."
          disabled={phase === "pending"}
        />
        <StepLink
          to="/ats-booster"
          icon={ZapIcon}
          title="Run an ATS boost"
          body="Score a resume and fix what applicant tracking systems flag."
          disabled={phase === "pending"}
        />
      </div>
      <Button variant="outline" className="mt-3 w-full" render={<Link to="/credits" />}>
        View credits and billing
      </Button>
    </div>
  );
}

function StepLink({
  to,
  icon: Icon,
  title,
  body,
  disabled,
}: {
  to: "/resume-tailor" | "/ats-booster";
  icon: typeof Wand2Icon;
  title: string;
  body: string;
  disabled?: boolean;
}) {
  return (
    <Link
      to={to}
      aria-disabled={disabled}
      className={cn(
        "group flex flex-col rounded-xl border border-border bg-muted/30 p-4 transition-colors hover:bg-muted/60",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground">
          <Icon className="mr-1.5 inline size-3.5 align-[-0.15em] text-violet-500" aria-hidden="true" />
          {title}
        </span>
        <ArrowRightIcon
          className="size-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </span>
      <span className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{body}</span>
    </Link>
  );
}
